// src/pages/Farmer/FarmersByVillage.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import farmerVisitService from '../../services/farmerVisit.service';
import Toast from '../../components/Toast/Toast';
import './FarmerVisitPages.css';

const formatDateTime = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const FarmersByVillage = () => {
  const navigate  = useNavigate();
  const { village } = useParams();
  const location  = useLocation();

  const isFollowup = location.pathname.includes('followup');
  const visitType  = isFollowup ? 'FOLLOWUP' : 'FIRST_VISIT';
  const pageTitle  = isFollowup ? 'Followup Visit' : 'First Visit';
  const decodedVillage = decodeURIComponent(village);

  const [farmers, setFarmers]   = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [toast, setToast]       = useState(null);

  useEffect(() => {
    fetchFarmers();
  }, [search]);

  const fetchFarmers = async () => {
    setLoading(true);
    try {
      const res = await farmerVisitService.getFarmersByVillage(decodedVillage, visitType, search);
      if (res.success && res.data) setFarmers(res.data);
    } catch (_) {
      setToast({ message: 'Failed to fetch farmers', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFarmerClick = (farmer) => {
    const path = isFollowup
      ? `/farmer/farmer-visit/village/${village}/farmer/${farmer.id}`
      : `/farmer/first-visit/village/${village}/farmer/${farmer.id}`;
    navigate(path, { state: { farmer } });
  };

  const handleBack = () => {
    navigate(isFollowup ? '/farmer/farmer-visit' : '/farmer/first-visit');
  };

  return (
    <div className="fv-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Breadcrumb */}
      <nav className="fv-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span>
        <span>Manage Farmer</span>
        <span>›</span>
        <button className="fv-breadcrumb-btn" onClick={handleBack}>{pageTitle}</button>
        <span>›</span>
        <span>{decodedVillage}</span>
      </nav>

      {/* Header */}
      <div className="fv-header">
        <div>
          <h4 className="fv-title">Farmers List</h4>
          <p className="fv-subtitle">{decodedVillage}</p>
        </div>
        <button className="fv-btn-back" onClick={handleBack}>← Back</button>
      </div>

      {/* Search */}
      <div className="fv-search-bar">
        <input
          type="text"
          className="fv-search-input"
          placeholder="Search for Farmers.."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="fv-table-container">
        {loading ? (
          <div className="fv-loading">Loading farmers...</div>
        ) : (
          <table className="fv-table">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Farmer Name</th>
                <th>Mobile</th>
                {isFollowup && <th>Last Visited By</th>}
                {isFollowup && <th>Last Visit</th>}
              </tr>
            </thead>
            <tbody>
              {farmers.length > 0 ? (
                farmers.map((farmer, index) => (
                  <tr
                    key={farmer.id}
                    className="fv-farmer-row"
                    onClick={() => handleFarmerClick(farmer)}
                  >
                    <td className="fv-sr">{index + 1}</td>
                    <td className="fv-farmer-link">
                      {farmer.farmerName}
                    </td>
                    <td>{farmer.mobileNo}</td>
                    {isFollowup && (
                      <td className="fv-last-visited">
                        {farmer.lastVisitedBy || '—'}
                      </td>
                    )}
                    {isFollowup && (
                      <td className="fv-last-time">
                        {formatDateTime(farmer.lastVisitTime)}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={isFollowup ? 5 : 3}
                    className="fv-no-data"
                  >
                    {search
                      ? `No farmers found for "${search}"`
                      : isFollowup
                      ? 'No farmers with previous visits in this village'
                      : 'No farmers found in this village'
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FarmersByVillage;